import React, { useState } from "react";
import { createPortal } from "react-dom";
import { PathwayFormGroup, PathwayLabel } from "../../StyledComponents/PathwayForm";

import PropTypes from "prop-types";
import styled from "styled-components";
import usePathwayInputGroup from "../../../_hooks/usePathwayInputGroup";
import MandatoryStar from "../MandatoryStar";
import { PathwayTooltip } from "../PathwayTooltip";
import PathwaySelectMenu from "./PathwaySelectMenu";
import { TOOLTIP_SURFACE } from "../../StyledComponents/tooltipSurface";

const PathwaySelectInputGroup = (props) => {
    const [errorTooltip, setErrorTooltip] = useState(null);

    const { valid, mandatory, isNotFilled, handleChange, onBlur } = usePathwayInputGroup(
        props.name,
        props.value,
        props.needsToBeFilled,
        props.submitted,
        props.autoFocus,
        props.triggerOnBlur,
        props.inputValidation,
        props.handleChange
    );

    const placeholder = (() => {
        if (props.showNullOption === false) return null;
        if (props.placeholder !== undefined) return props.placeholder;
        return "Bitte wählen";
    })();

    const errorText = (() => {
        if (mandatory && isNotFilled && props.invalidInputMessage && !props.inputValidation) {
            return props.invalidInputMessage;
        }
        if (mandatory && isNotFilled) {
            return "Eine Auswahl wird benötigt.";
        }
        if (!valid) {
            return props.invalidInputMessage || null;
        }
        return null;
    })();

    const handleErrorIconMouseEnter = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setErrorTooltip({ text: errorText, top: r.bottom + 6, left: r.left });
    };

    const handleErrorIconMouseLeave = () => setErrorTooltip(null);

    return (
        <PathwayFormGroup data-cy={props.dataCy ? props.dataCy + "-form" : undefined}>
            {(props.label || props.tooltip) && (
                <TooltipInputGroup>
                    <LabelWrapper>
                        <LabelParagraph data-cy={props.dataCy ? props.dataCy + "-label" : undefined}>
                            {props.label}
                        </LabelParagraph>{" "}
                        {mandatory && <MandatoryStar />}
                    </LabelWrapper>
                    {props.tooltip && (
                        <PathwayTooltip id={props.dataCy + "-tooltip"} iconSize="var(--iconSize-xs)">
                            {props.tooltip}
                        </PathwayTooltip>
                    )}
                </TooltipInputGroup>
            )}

            <SelectWrapper>
                <PathwaySelectMenu
                    name={props.name}
                    value={props.value}
                    onChange={handleChange}
                    onBlur={onBlur}
                    disabled={props.disabled}
                    invalid={!valid}
                    dataCy={props.dataCy}
                    ariaLabel={props.label || props.ariaLabel}
                    className={props.mono ? "pw-mono" : undefined}
                >
                    {props.nullOptionValue ? (
                        <option value="">{props.nullOptionValue}</option>
                    ) : (
                        placeholder && (
                            <option value="" disabled>
                                {placeholder}
                            </option>
                        )
                    )}
                    {props.children}
                </PathwaySelectMenu>

                {errorText && (
                    <ErrorIcon
                        type="button"
                        data-cy={props.dataCy ? props.dataCy + "-error-icon" : undefined}
                        onMouseEnter={handleErrorIconMouseEnter}
                        onMouseLeave={handleErrorIconMouseLeave}
                        onFocus={handleErrorIconMouseEnter}
                        onBlur={handleErrorIconMouseLeave}
                        aria-label={errorText}
                    >
                        <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                    </ErrorIcon>
                )}
            </SelectWrapper>

            {errorTooltip &&
                createPortal(
                    <div
                        style={{
                            position: "fixed",
                            top: errorTooltip.top,
                            left: errorTooltip.left,
                            zIndex: 99999,
                            padding: "4px 10px",
                            maxWidth: 320,
                            pointerEvents: "none",
                            wordBreak: "break-word",
                            ...TOOLTIP_SURFACE,
                            backgroundColor: "var(--pw-danger)",
                        }}
                    >
                        {errorTooltip.text}
                    </div>,
                    document.body
                )}
        </PathwayFormGroup>
    );
};

export default PathwaySelectInputGroup;

PathwaySelectInputGroup.propTypes = {
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleChange: PropTypes.func.isRequired,
    dataCy: PropTypes.string,
    mono: PropTypes.bool,
    invalidInputMessage: PropTypes.string,
    needsToBeFilled: PropTypes.bool,
    autoFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    inputValidation: PropTypes.func,
    triggerOnBlur: PropTypes.func,
    placeholder: PropTypes.string,
    showNullOption: PropTypes.bool,
    nullOptionValue: PropTypes.string,
};

const SelectWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const ErrorIcon = styled.button`
    position: absolute;
    /* The select has a built-in caret on the far right; put the icon just left of it (~32px from right) */
    right: 32px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: none;
    color: var(--pw-danger-fg);
    font-size: 13px;
    cursor: default;
    line-height: 1;
    /* Prevent the select's own click area from swallowing the hover */
    pointer-events: all;
    z-index: 1;
`;

// Kept for a11y/future reference. Not rendered.
// eslint-disable-next-line no-unused-vars
const InvalidLabel = styled(PathwayLabel)`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 0;
    padding-top: var(--gap-s);
    color: var(--pw-danger-fg);
    font-size: var(--pw-text-sm);
    font-weight: var(--pw-weight-regular);
`;

export const LabelWrapper = styled(PathwayLabel)`
    display: grid;
    grid-template-columns: auto min-content;
    align-items: center;
`;

export const LabelParagraph = styled.p`
    margin: 0;
    font-size: var(--pw-text-sm);
    font-weight: var(--pw-weight-regular);
    color: var(--text-heading);
    line-height: var(--pw-leading-snug);
`;

const TooltipInputGroup = styled.div`
    display: grid;
    grid-template-columns: max-content min-content;
    column-gap: var(--gap-s);
`;
